/**
 * @link      https://karmabunny.com.au
 * @copyright Copyright (c) 2020 Karmabunny
 */

/** 30 seconds. */
const TIMEOUT = 30_000;

export type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type Query = Record<string, any> | URLSearchParams;

export type Body = Query | FormData;

export interface ApiRequest {
    method: string;
    endpoint: string;
    query?: Query;
    headers?: Record<string, string>,
    body?: any;
}


/**
 *
 * @param params
 */
export function createQuery(params?: Query): string {
    if (!params) return '';

    if (!(params instanceof URLSearchParams)) {
        params = new URLSearchParams(params);
    }

    const query = params.toString();
    return query ? '?' + query : '';
}


/**
 *
 * @param body
 * @returns [body, content-type]
 */
export function createBody(body: Body): [any, string] {
    if (body instanceof URLSearchParams) {
        return [
            body.toString(),
            'application/x-www-form-urlencoded',
        ];
    }

    if (body instanceof FormData) {
        return [
            body,
            'multipart/form-data',
        ];
    }

    return [
        JSON.stringify(body),
        'application/json; charset=UTF-8',
    ];
}


/**
 *
 * @param timeout
 * @returns
 */
async function sleep(timeout: number) {
    return new Promise<void>(resolve => {
        window.setTimeout(resolve, timeout);
    })
}


/**
 *
 *
 * Errors galore!:
 * - ApiError on 400+ status codes
 * - TimeoutError if we're too slow (looking at you RMS)
 * - SyntaxError on malformed JSON
 * - TypeError if the network is dead
 *
 * @param req
 * @throws {ApiError|TimeoutError|SyntaxError|TypeError}
 */
export async function api<T = any>(req: ApiRequest): Promise<T> {

    const abort = new AbortController();

    const { endpoint, method } = req;
    const params = createQuery(req.query);

    // Base headers.
    const headers: Record<string, string> = {
        'Accept': 'application/json',
    };

    let body: any = null;

    // Encode the body.
    if (req.body) {
        let contentType: string;
        [body, contentType] = createBody(req.body);
        headers['Content-Type'] = contentType;
    }

    // Additional headers.
    if (req.headers) {
        Object.assign(headers, req.headers);
    }

    return await Promise.race([
        // Do a request, although catch status code errors and throw them.
        // Always convert to JSON.
        (async () => {
            const res = await fetch(endpoint + params, {
                method, headers, body,
                signal: abort.signal,
                credentials: 'include',
                mode: 'cors',
            });

            if (!res.ok) throw await newApiError(req, res);

            // TODO we could conditionally convert to JSON based on the
            // 'Accept' header.
            return await res.json();
        })(),

        // Race it. We're on the clock.
        (async () => {
            await sleep(TIMEOUT);
            abort.abort();
            throw await newApiError(req, null);
        })(),
    ]);
}


/**
 *
 */
export interface ApiError extends Error {
    name: "ApiError";
    status: number;
    url: string;
    res?: Response;
}


/**
 *
 * @param req
 * @param res
 * @returns
 */
export async function newApiError(req: ApiRequest, res: Response | null): Promise<ApiError> {
    const url = req.endpoint + createQuery(req.query);

    let message = '';

    // API responses.
    if (res) {
        try {
            const body = await res.json();

            // Validation errors.
            if (body.status && body.validations) {
                message = Object.values(body.validations as Record<string, string | string[]>)
                    .map(message => (
                        typeof message === "string"
                        ? message
                        : message.join(', ')
                    ))
                    .join(', ');
            }
            // Regular errors.
            else if (body.message) {
                message = body.message;
            }
            // Rando errors.
            else if (typeof body.error === "string") {
                message = body.error;
            }
        }
        catch (e) {}
    }

    // Error parsing JSON body (or timeout).
    if (!message) {
        message = `${req.method} ${url}`;

        // API error.
        if (res) {
            message += ` ${res.status} (${res.statusText})`;
        }
        // Timeouts.
        else {
            const seconds = (TIMEOUT / 1000).toFixed(0);
            message += ` - timeout (${seconds}s)`;
        }
    }

    // Built it.
    const error = new Error(message) as ApiError;
    error.name = "ApiError";
    error.status = 0;
    error.url = url;

    // Additional for API errors.
    if (res) {
        error.status = res.status;
        error.res = res;
    }

    return error;
}


/**
 *
 * @param test
 * @returns
 */
export function isApiError(test: unknown): test is ApiError {
    return !!test && test instanceof Error && test.name === "ApiError";
}


/**
 *
 * @param test
 */
export function assertApiError(test: unknown): asserts test is ApiError {
    if (!isApiError(test)) throw test;
}
